import type { InputSignal, OnInit } from '@angular/core';
import { DestroyRef, Directive, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type { AdvancedBorderSettings } from '@components/sp-advanced-border-settings/type/advanced-border-settings.type';
import type { BackgroundAtDevice, BackgroundFile, Device } from '@sites/entity-core';
import {
    backgroundAtDevice,
    createBorderSide,
    createShapeDivider,
    DEVICES,
    isBorderIndependent,
    SIDES,
} from '@sites/entity-core';
import type { BlockDefaultForm, BlockShapeDividerEdge, BlockZeroForm } from '@sites/entity-signal-form';

import type { NormalizedDefaultBlockEntity, NormalizedZeroBlockEntity } from '@web/types';

import { WidgetDataSubTypes, WidgetsDataTypes } from '@common/enums';
import { EBlockShapeDividerFlip } from '@common/enums/block-shape-dividers-type.enum';

import { environment } from '@web-env/environment';
import { AppFacade } from '@web/store/app/app.facade';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { WebBlockFormService } from '@web/core/services/web-block-form.service';
import { ImageEditorService } from '@web/shared/modals/image-editor-modal/image-editor.service';
import { UseUnsplash } from '@web/shared/hooks/use-unsplash.hook';

import { AbstractBaseSignalSettingsComponent } from './abstract-base-signal-settings.component';

/** The fifteen drawn shapes each divider can take, by their 1-based index. */
const SHAPE_DIVIDER_VARIANTS = Array.from({ length: 15 }, (_, index) => String(index + 1));

/**
 * Enough of a responsive leaf's shape to tell it apart from the `FieldState` that
 * calling one returns. Not `FieldTree<T>` per breakpoint: that type is invariant
 * in its model, so every loose type argument the call sites pass would have to be
 * tightened to the schema's exact shape first.
 */
export type ResponsiveNode = { desktop?: unknown; tablet?: unknown; mobile?: unknown };

/**
 * What the ordinary block's and the canvas block's panels share. Edits go straight
 * into the block signal form (`WebBlockFormService`), which owns the dispatch and
 * the history entry — a panel only maps controls onto fields.
 *
 * **One device section, not three.** Every styleable field is per-breakpoint, and
 * the panels used to carry a full copy of the controls for each — with a matching
 * copy of every getter and setter, and a hand-written mirror from the desktop
 * copy into the other two. Here the breakpoint is a value: {@link readDevice}
 * says which one the controls show and {@link write} says which ones an edit
 * lands in.
 *
 * The two differ in the box: an ordinary block is sized by its padding, a canvas
 * by an explicit height, since its widgets are placed absolutely and give it
 * nothing to size to.
 */
@Directive()
export abstract class AbstractBlockSettingsComponent<
    TForm extends BlockDefaultForm | BlockZeroForm,
    TEntity extends NormalizedDefaultBlockEntity | NormalizedZeroBlockEntity,
>
    extends AbstractBaseSignalSettingsComponent
    implements OnInit
{
    public abstract readonly entity: InputSignal<TEntity>;

    private readonly appFacade = inject(AppFacade);
    private readonly siteFacade = inject(SiteFacade);
    private readonly blockFormService = inject(WebBlockFormService);
    private readonly imageEditorService = inject(ImageEditorService);
    private readonly useUnsplash = inject(UseUnsplash);
    private readonly destroyRef = inject(DestroyRef);

    protected form!: TForm;

    public readonly EBlockShapeDividerFlip = EBlockShapeDividerFlip;
    public readonly shapeDividerVariants = SHAPE_DIVIDER_VARIANTS;

    public readonly isAdmin = toSignal(this.appFacade.isAdmin$, { initialValue: false });

    /** Which breakpoint's tab is open, from the shared device toggle. */
    public readonly device = toSignal(this.layoutFacade.deviceMode$ as never, {
        initialValue: 'desktop' as Device,
    });

    public ngOnInit(): void {
        this.form = this.blockFormService.getOrCreateScoped(this.entity().id, this.destroyRef) as TForm;
    }

    /** Copies the box field the subtype adds — its padding or its height — from desktop onto a tab. */
    protected abstract copyDesktopBox(device: Device): void;

    // --- Device plumbing ------------------------------------------------------

    public readonly syncWithDesktop = computed<boolean>(() => Boolean(this.form.syncWithDesktop().value()));

    /**
     * The breakpoint the controls read. With synchronisation on, the tablet and
     * mobile tabs show the desktop values — they are what all three share.
     */
    protected readonly readDevice = computed<Device>(() =>
        this.syncWithDesktop() ? 'desktop' : (this.device() as Device)
    );

    /**
     * Applies an edit to every breakpoint it should reach: all three while
     * synchronisation is on, otherwise only the open tab. One transaction, so the
     * three writes land as a single history entry.
     */
    protected write(apply: (device: Device) => void): void {
        const devices = this.syncWithDesktop() ? DEVICES : [this.device() as Device];

        this.form.transact(() => {
            devices.forEach(apply);
            this.form.compact();
        });
    }

    // --- Reads ----------------------------------------------------------------

    /**
     * Reads a responsive leaf at the tab the panel is showing, falling back to
     * desktop where a breakpoint has none of its own.
     *
     * Typed rather than `any` on purpose: the argument is the FieldTree *node*,
     * not the FieldState a call on it returns, and the two look alike enough at a
     * call site that `this.form.padding()` slipped past review once already —
     * with `any` neither `tsc` nor the linter had anything to say, and the panel
     * threw on open.
     */
    protected at = <T>(responsive: ResponsiveNode | undefined): T | undefined => {
        if (!responsive) {
            // The block's optional background layers, on a block stored without them.
            return undefined;
        }

        const device = this.readDevice();
        const node = responsive as Record<string, () => { value: () => T }>;

        return (node[device] ?? node['desktop'])().value();
    };

    public readonly backgroundColor = computed<string>(() => {
        const color = this.at<string | null>(this.form.background.color);

        return color ?? '';
    });

    /** What the image picker edits, at the tab the panel is showing. */
    public readonly background = computed(() => backgroundAtDevice(this.form.background().value(), this.readDevice()));
    public readonly backgroundImage = computed(() => this.background().image);
    public readonly backgroundVideo = computed(
        () => this.at<BackgroundFile | null>(this.form.background.video) ?? null
    );

    public readonly isBorder = computed<boolean>(() => Boolean(this.at(this.form.border.isEnabled)));

    public readonly borderSettings = computed<AdvancedBorderSettings>(() => {
        const border = this.form.border;
        const sides: Record<string, unknown> = {};

        SIDES.forEach((side) => {
            // A contour that is off stores no sides; the panel shows the ones switching it on would seed.
            const contour =
                this.at<{ width: number; style: string; color: string }>(border[side]) ?? createBorderSide();
            const key = side[0].toUpperCase() + side.slice(1);

            sides[`border${key}Width`] = contour?.width;
            sides[`border${key}Style`] = contour?.style;
            sides[`border${key}Color`] = contour?.color;
        });

        return {
            isActive: this.isBorder(),
            // Derived from the sides, with the switch's own answer winning while
            // the panel is open — see `resolveIndependentBorders`.
            isIndependentBorders: this.resolveIndependentBorders(
                this.readDevice(),
                isBorderIndependent(border().value(), this.readDevice())
            ),
            ...sides,
        } as AdvancedBorderSettings;
    });

    // A divider that is off keeps only its switch; the panel shows the values switching it on would seed.
    public readonly topDivider = computed(() => ({
        ...createShapeDivider(),
        ...this.at<any>(this.form.shapeDividers.top),
    }));
    public readonly bottomDivider = computed(() => ({
        ...createShapeDivider(),
        ...this.at<any>(this.form.shapeDividers.bottom),
    }));

    public readonly pinOnTheTop = computed<boolean>(() => this.form.pin().value() === 'top');
    public readonly pinOnTheBottom = computed<boolean>(() => this.form.pin().value() === 'bottom');
    public readonly hidden = computed(() => this.form.hidden().value());

    /** Falls back to a slice of the id, which is what the user sees by default. */
    public readonly displayId = computed<string>(
        () => this.entity().meta.displayId || this.entity().id.substring(0, 8)
    );

    // --- Writes ---------------------------------------------------------------

    public setSynchronizeStyle(): void {
        const isOn = this.syncWithDesktop();

        // Turning it on copies desktop over the other two, so what the user sees
        // on the desktop tab is what every breakpoint gets from then on.
        this.form.transact(() => {
            if (!isOn) {
                this.copyDesktopToOtherDevices();
            }

            this.form.setSyncWithDesktop(!isOn);
            // With synchronisation on, the copies above repeat desktop and are dropped.
            this.form.compact();
        });
    }

    private copyDesktopToOtherDevices(): void {
        const background = this.form.background;
        const border = this.form.border;
        const dividers = this.form.shapeDividers;
        const targets: Device[] = ['tablet', 'mobile'];
        const read = <T>(node: any): T => node.desktop().value();
        // Every layer, not only the image: a layer left out keeps a tab's own value, which synchronisation
        // then neither shows nor clears.
        const desktopBackground = backgroundAtDevice(background().value(), 'desktop');

        targets.forEach((device) => {
            this.copyDesktopBox(device);
            this.form.setBackgroundColor(read(background.color), device);
            this.form.setBackgroundAtDevice(desktopBackground, device);
            this.form.setBackgroundScrollEffect(desktopBackground.scrollEffect, device);
            this.form.setBackgroundVideo(background.video ? read(background.video) : null, device);
            this.form.setBorderEnabled(read<boolean>(border.isEnabled), device);
            // Nothing to copy for the independent-sides switch: the four sides
            // carry the answer, and they are copied right below.
            // A contour that is off stores no sides — nothing to copy then.
            SIDES.forEach((side) => border[side] && this.form.setBorderSide(side, read(border[side]), device));
            (['top', 'bottom'] as const).forEach((edge) =>
                this.form.setShapeDivider(edge, read(dividers[edge]), device)
            );
        });
    }

    public setBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        const color = presetName ?? value ?? null;

        this.write((device) => this.form.setBackgroundColor(color, device));
    }

    public setBackground(background: BackgroundAtDevice): void {
        const { image } = background;

        // A picked image and a background video are exclusive, so one clears the
        // other. The resize flag and the resize request belong to the desktop
        // copy — only it is ever served at full width.
        const withResize = image ? { ...image, hasResize: true } : image;
        const isDesktopWrite = this.syncWithDesktop() || this.device() === 'desktop';

        this.write((device) => {
            this.form.setBackgroundAtDevice({ ...background, image: isDesktopWrite ? withResize : image }, device);
            this.form.setBackgroundVideo(null, device);
            this.form.setBackgroundScrollEffect(background.scrollEffect, device);
        });

        if (image && isDesktopWrite) {
            this.siteFacade.resizeImage({
                path: image.path,
                url: `${environment.fileManagerCdnUrl}${image.url}`,
            });
        }
    }

    public setBackgroundVideo(video: BackgroundFile | null): void {
        this.write((device) => {
            this.form.setBackgroundVideo(video, device);
            // Exclusive with a picked image, the same way round as above.
            if (video) this.form.setBackgroundImage(null, device);
        });
    }

    // --- Contour --------------------------------------------------------------

    public setIsBorder(value: boolean): void {
        this.write((device) => this.form.setBorderEnabled(value, device));
    }

    public setAdvancedBorderSettings(settings: AdvancedBorderSettings): void {
        this.write((device) => {
            this.rememberIndependentBorders(device, Boolean(settings.isIndependentBorders));

            SIDES.forEach((side) => {
                const key = side[0].toUpperCase() + side.slice(1);
                const patch = settings as unknown as Record<string, never>;

                this.form.setBorderSide(
                    side,
                    {
                        width: patch[`border${key}Width`],
                        style: patch[`border${key}Style`],
                        color: patch[`border${key}Color`],
                    },
                    device
                );
            });
        });
    }

    // --- Shape dividers -------------------------------------------------------

    public setDivider(edge: BlockShapeDividerEdge, patch: Record<string, unknown>): void {
        this.write((device) => this.form.setShapeDivider(edge, patch as never, device));
    }

    public setDividerEnabled(edge: BlockShapeDividerEdge, isEnabled: boolean): void {
        this.setDivider(edge, { isEnabled });
    }

    public setDividerColor(edge: BlockShapeDividerEdge, { value, presetName }: ColorPickerEventPayload): void {
        this.setDivider(edge, { color: presetName ?? value });
    }

    /** The two flips are independent toggles, each on or back to none. */
    public toggleDividerFlip(edge: BlockShapeDividerEdge, axis: 'horizontal' | 'vertical'): void {
        const divider = edge === 'top' ? this.topDivider() : this.bottomDivider();
        const key = axis === 'horizontal' ? 'horizontalFlip' : 'verticalFlip';
        const on = axis === 'horizontal' ? EBlockShapeDividerFlip.HORIZONTAL : EBlockShapeDividerFlip.VERTICAL;

        this.setDivider(edge, { [key]: divider?.[key] === on ? EBlockShapeDividerFlip.NONE : on });
    }

    // --- Block-wide switches --------------------------------------------------

    /** Hiding is one switch per breakpoint, not a value read at one. */
    public toggleHiddenOn(device: Device): void {
        this.form.setHidden(device, !this.hidden()?.[device]);
    }

    public setDisplayId(value: string): void {
        this.form.setDisplayId(value);
    }

    // --- Modals ---------------------------------------------------------------

    public showEditImageModal(): void {
        this.imageEditorService.showEditBackgroundImageModal(
            this.backgroundImage() as never,
            this.entity() as never,
            WidgetDataSubTypes.block,
            this.readDevice()
        );
    }

    public openUnsplashModal(): void {
        this.useUnsplash.openUnsplashModal({
            dataType: WidgetsDataTypes.backgroundImage,
            dataSubType: WidgetDataSubTypes.block,
            entity: this.entity() as never,
            device: this.readDevice(),
            isSync: this.syncWithDesktop(),
        });
    }
}
